<template>
  <div
    v-if="user"
    class="keyword_positions_container default-container-width"
  >
    <div class="title_row">
      <div class="title-wrap relative">
        <div class="title">
          <h2>Keyword Tracker</h2>
          <div>
            <button
              v-if="showHelpVideosAndTours"
              class="btn btn_sm primary_btn watch-video"
              @click="showHelpVideo = true"
            >
              <i class="video_icon" />
              <span>Help</span>
            </button>
            <button
              class="btn btn_sm primary_btn watch-video"
              @click="showAverageModal = true"
            >
              <i class="fas fa-chart-line" />
              <span>Avg Ranking</span>
            </button>
            <button
              class="btn btn_sm primary_btn watch-video"
              @click="showCompetitionModal = true"
            >
              <i class="fas fa-rocket" />
              <span>Competition</span>
            </button>
            <a
              class="btn btn_sm primary_btn watch-video"
              href="/keyword-scan"
            >
              <i class="fab fa-searchengin" />
              <span>Keyword Scan</span>
            </a>
            <button
              class="btn btn_sm primary_btn watch-video"
              @click="settings = true"
            >
              <svg
                data-v-e7d431e0=""
                fill="none"
                height="18"
                viewBox="0 0 18 18"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="9.00004"
                  cy="9"
                  data-v-e7d431e0=""
                  r="2.66667"
                  stroke="#FFFFFF"
                  stroke-width="1.41667"
                />
                <path
                  d="M7.78485 2.36124C8.33493 1.44326 9.66515 1.44326 10.2152 2.36124L10.5825 2.9741C10.9033 3.50945 11.5366 3.7718 12.142 3.62009L12.8351 3.44641C13.8732 3.18627 14.8138 4.12688 14.5536 5.16495L14.3799 5.858C14.2282 6.46339 14.4906 7.09675 15.0259 7.41755L15.6388 7.7848C16.5568 8.33489 16.5568 9.6651 15.6388 10.2152L15.0259 10.5824C14.4906 10.9032 14.2282 11.5366 14.3799 12.142L14.5536 12.835C14.8138 13.8731 13.8732 14.8137 12.8351 14.5536L12.142 14.3799C11.5366 14.2282 10.9033 14.4905 10.5825 15.0259L10.2152 15.6388C9.66515 16.5567 8.33493 16.5567 7.78485 15.6388L7.4176 15.0259C7.0968 14.4905 6.46344 14.2282 5.85804 14.3799L5.165 14.5536C4.12693 14.8137 3.18632 13.8731 3.44646 12.835L3.62013 12.142C3.77184 11.5366 3.5095 10.9032 2.97414 10.5824L2.36128 10.2152C1.44331 9.6651 1.44331 8.33489 2.36128 7.7848L2.97414 7.41755C3.5095 7.09675 3.77184 6.46339 3.62013 5.858L3.44646 5.16495C3.18632 4.12688 4.12693 3.18627 5.165 3.44641L5.85804 3.62009C6.46344 3.7718 7.0968 3.50945 7.4176 2.9741L7.78485 2.36124Z"
                  data-v-e7d431e0=""
                  stroke="#FFFFFF"
                  stroke-width="1.41667"
                />
              </svg>
              <transition name="fade">
                <ul
                  v-show="settings"
                  class="select_dropdown"
                  @mouseleave="settings = false"
                >
                  <li>
                    <a
                      href="/export/pdf/keywords"
                      target="_blank"
                    >
                      <i class="far fa-file-pdf" />
                      <span>Export PDF</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="/export/csv/keywords"
                      target="_blank"
                    >
                      <i class="far fa-file-csv" />
                      <span>Export CSV</span>
                    </a>
                  </li>
                  <li v-if="canEditLocation">
                    <a href="/keywords/manage">
                      <i class="fad fa-tasks" />
                      <span>Manage</span>
                    </a>
                  </li>
                </ul>
              </transition>
            </button>
          </div>
        </div>
        <div class="description">
          Keep track of your keyword rankings in Google below. Monitoring these rankings also helps you understand how well your content is positioned for future AI visibility. You can also check your average ranking and top competitors using the buttons at the top of the page.
        </div>
      </div>
      <div
        id="keywords_table"
        class="white_box"
      >
        <div class="d-flex justify-content-end">
          <custom-search @search="setSearchTerm" />
        </div>

        <div class="table_wrapper">
          <table class="keywords_table head">
            <thead>
              <tr>
                <td
                  rowspan="2"
                  width="20%"
                >
                  <div
                    :class="'sort_title '+(sort == 'keyword.keyword' ? sortDirection : '')"
                    @click="setSort('keyword.keyword')"
                  >
                    <span>Keyword</span>
                    <i class="icon arrow_down" />
                  </div>
                </td>
                <td rowspan="2">
                  <div
                    :class="'sort_title '+(sort == 'ranking_url' ? sortDirection : '')"
                    @click="setSort('ranking_url')"
                  >
                    <span>URL</span>
                    <i class="icon arrow_down" />
                  </div>
                </td>
                <td
                  colspan="3"
                  width="40%"
                >
                  <div>
                    <span>Position</span>
                  </div>
                </td>
                <td
                  rowspan="2"
                  width="10%"
                >
                  <div
                    :class="'sort_title center '+(sort == 'search_volume' ? sortDirection : '')"
                    @click="setSort('search_volume')"
                  >
                    <i class="icon placeholder" />
                    <span>Search Vol</span>
                    <i class="icon arrow_down" />
                  </div>
                </td>
                <td
                  rowspan="2"
                  width="6%"
                >
                  <span>Analysis</span>
                </td>
              </tr>
              <tr>
                <td class="positions">
                  <div
                    :class="'sort_title center '+(sort == 'current_position' ? sortDirection : '')"
                    @click="setSort('current_position')"
                  >
                    <i class="icon placeholder" />
                    <span>Current</span>
                    <i class="icon arrow_down" />
                  </div>
                </td>
                <td class="positions">
                  <div
                    :class="'sort_title center '+(sort == 'previous_week_diff' ? sortDirection : '')"
                    @click="setSort('previous_week_diff')"
                  >
                    <i class="icon placeholder" />
                    <span>Last Week</span>
                    <i class="icon arrow_down" />
                  </div>
                </td>
                <td class="positions">
                  <div
                    :class="'sort_title center '+(sort == 'previous_month_diff' ? sortDirection : '')"
                    @click="setSort('previous_month_diff')"
                  >
                    <i class="icon placeholder" />
                    <span>Last Month</span>
                    <i class="icon arrow_down" />
                  </div>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="keyword in sortedKeywords"
                :key="keyword.id"
              >
                <td class="keyword">
                  {{ keyword.keyword.keyword }}
                </td>
                <td>
                  <a
                    :href="keyword.ranking_url"
                    :title="urlPathname(keyword.ranking_url)"
                    class="table_link tooltipster"
                    target="_blank"
                  >
                    {{ urlPathname(keyword.ranking_url) }}
                  </a>
                  <i
                    v-if="assignedToDifferentPage(keyword)"
                    :title="generateAssignedToDifferentPageTitle(keyword)"
                    class="fad fa-exclamation-triangle text-warning tooltipster"
                  />
                </td>
                <td class="positions">
                  {{
                    keyword.current_position
                      ? keyword.current_position > 100 ? '100+' : keyword.current_position
                      : ``
                  }}
                  <i
                    v-if="!keyword.current_position"
                    class="fa fa-btn fa-spinner fa-spin"
                  />
                  <button
                    v-if="isAdmin && !keyword.current_position "
                    class="btn btn_sm primary_btn refresh tooltipster"
                    title="Refresh the keyword ranking for today, please click and wait up to 1 hour before re-clicking"
                    @click="refreshKeyword(keyword)"
                  >
                    <i class="fas fa-sync" />
                  </button>
                </td>
                <td class="positions">
                  <div
                    :class="`position_change ${calcVariation(keyword.current_position, keyword.previous_week)} tooltipster`"
                    :title="keyword.previous_week == null ? `No ranking for this date` : `Your position on this day was ${keyword.previous_week}`"
                  >
                    <i :class="`icon ${calcVariation(keyword.current_position, keyword.previous_week)}_arrow`" />
                    <span>{{ getPositionChange(keyword.current_position, keyword.previous_week) }}</span>
                    <i class="icon placeholder" />
                  </div>
                </td>
                <td class="positions">
                  <div
                    :class="`position_change ${calcVariation(keyword.current_position, keyword.previous_month)} tooltipster`"
                    :title="keyword.previous_month == null ? `No ranking for this date` : `Your position on this day was ${keyword.previous_month}`"
                  >
                    <i :class="`icon ${calcVariation(keyword.current_position, keyword.previous_month)}_arrow`" />
                    <span>{{ getPositionChange(keyword.current_position, keyword.previous_month) }}</span>
                    <i class="icon placeholder" />
                  </div>
                </td>
                <td>
                  {{ keyword.search_volume > 0 ? keyword.search_volume.toLocaleString() : '< 10' }}
                </td>
                <td>
                  <a
                    :href="`/keywords/${keyword.id}`"
                    class="btn btn_md secondary_btn view_analysis"
                  >
                    <i class="fad fa-eye" />
                  </a>
                </td>
              </tr>
              <tr>
                <td
                  v-if="loadingKeywords"
                  class="text-center"
                  colspan="8"
                >
                  <i
                    v-if="loadingKeywords"
                    class="fas fa-spinner fa-spin"
                  />
                  {{ loadingKeywords ? 'Loading keywords' : 'No keywords found' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <help-video-modal v-if="showHelpVideo" />
  <average-modal v-if="showAverageModal" />
  <competition-modal v-if="showCompetitionModal" />
</template>

<script>
import _ from 'lodash';
import {mapActions, mapState, mapGetters} from 'vuex';
import HelpVideoModal from './overview/modals/HelpVideoModal.vue';
import AverageModal from './overview/modals/AverageModal.vue';
import CompetitionModal from './overview/modals/CompetitionModal.vue';
import CustomSearch from "../../reports/components/global/forms/CustomSearch.vue";

export default {
  components: {
    CustomSearch,
    HelpVideoModal,
    AverageModal,
    CompetitionModal,
  },
  data() {
    return {
      showHelpVideo: false,
      showAverageModal: false,
      showCompetitionModal: false,
      showUntrackedKeywordsModal: false,
      searchTerm: '',
      sort: 'current_position',
      sortDirection: 'asc',
      settings: false,
      loadingKeywords: true,
    }
  },
  computed: {
    ...mapGetters('user', [
      'canEditLocation',
      'isAdmin',
    ]),
    ...mapState({
      keywords: (state) => state.keywords.keywords,
      user: (state) => state.user.user,
    }),
    ...mapGetters('whitelabel', [
      'showHelpVideosAndTours',
    ]),
    sortedKeywords() {
      let sortedKeywords = [];
      let sortIteratees = [];

      switch (this.sort) {
        case 'current_position':
          sortIteratees = [
            // If no current position, return 1000 to order it last
            (kw => {
              return kw.current_position || 1000
            }),
            'search_volume'
          ];
          break;
        case 'keyword':
          sortIteratees = [this.sort];
          break;
        default:
          sortIteratees = [
            this.sort,
            // If no current position, return 1000 to order it last
            (kw => {
              return kw.current_position || 1000
            }),
            'search_volume'
          ];
          break;
      }

      sortedKeywords = this.searchTerm.length
        ? _.filter(
            this.keywords,
            ({ keyword }) => keyword.keyword.toLowerCase().includes(this.searchTerm.toLowerCase())
          )
        : this.keywords;

      sortedKeywords = _.orderBy(sortedKeywords, sortIteratees, [this.sortDirection, 'asc', 'asc']);

      return sortedKeywords;
    }
  },
  async mounted() {
    const query = new URLSearchParams(location.search);

    if (query.has('modal') && query.get('modal') === 'ranking') {
      this.showAverageModal = true;
    }

    await this.fetchUser();
    this.fetchData().then(() => {
      this.loadingKeywords = false;
    });
  },
  methods: {
    ...mapActions({
      fetchUser: 'user/getUser',
      fetchData: 'keywords/fetchData',
      refreshKeywordRanking: 'keywords/refreshKeywordRanking',
    }),
    calcVariation(current, previous) {
      if (current === null || previous === null) return 'neutral';
      if (current < previous) return 'increase';
      if (current > previous) return 'decrease';
      return 'neutral';
    },
    getPositionChange(current, previous) {
      let value = 0;
      if (current === null || previous === null) {
        value = '-';
      } else if (current === previous) {
        value = current;
      } else if (current > previous) {
        value = current - previous;
      } else {
        value = previous - current;
      }
      if (value > 100) value = '100+';
      return value;
    },
    setSort(sortBy) {
      if (this.sort === sortBy) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sort = sortBy;
        switch (sortBy) {
          case 'keyword':
            this.sortDirection = 'asc';
            break;
          case 'search_volume':
            this.sortDirection = 'desc';
            break;
          case 'difficulty':
            this.sortDirection = 'desc';
            break;
        }
      }
    },
    setSearchTerm(term) {
      this.searchTerm = term
    },
    urlPathname(url) {
      if (!url) return '';
      const parsedUrl = new URL(url);
      return parsedUrl.pathname;
    },
    assignedToDifferentPage(keyword) {
      if (!keyword.assigned_page || !keyword.ranking_url) {
        return false
      }

      return !this.caseInsensitiveStringsMatch(keyword.assigned_page, keyword.ranking_url);
    },
    caseInsensitiveStringsMatch(stringA, stringB) {
      return typeof stringA === 'string' && typeof stringB === 'string'
        ? stringA.localeCompare(stringB, undefined, {sensitivity: 'base'}) === 0
        : stringA === stringB;
    },
    generateAssignedToDifferentPageTitle(keyword) {
      const string = `
                The URL that is ranking in Google for the keyword doesn't match the URL you have assigned.
                <br><br>
                <strong>Assigned Page:</strong><br>
                <a href="${keyword.assigned_page}" target='_blank'>${keyword.assigned_page}</a>
                <br><br>
                <strong>Ranking Page:</strong><br>
                <a href="${keyword.ranking_url}" target='_blank'>${keyword.ranking_url}</a>
            `;

      return string;
    },
    refreshKeyword(keywordObj) {
      const {id} = keywordObj;
      const {keyword} = keywordObj.keyword;
      this.refreshKeywordRanking(id)
          .then(() => {
            this.SweetToast.fire({
              icon: 'success',
              title: `Refreshing ${keyword}'s ranking position. This can take up to 1 hour.`
            });
            this.loading = true;
            this.fetchData().then(() => {
              this.loading = false;
            });
          }).catch(({response}) => {
        console.log(response);
        const {data: {message = null}} = response;
        this.SweetToast.fire({
          icon: 'error',
          title: `There was an error refreshing ${keyword}: ${message ? message : response.statusText}`
        });
      });
    },
  }
}
</script>

<style scoped>
.keyword_positions_container .title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title h2 {
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.042em;
  color: var(--primary-font-color);
  font-size: 2.5rem;
}

.title-wrap {
  margin-bottom: 2.5em;
}

.description {
  margin: 5px 0 10px 0;
  font-size: 1rem;
  line-height: 1.625;
  letter-spacing: -0.015625em;
  color: var(--secondary-font-color);
}

.watch-video {
  height: 42px;
  display: inline-flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
}

.watch-video span {
  font-weight: 400;
  line-height: 1.277;
  text-align: center;
  letter-spacing: -0.5px;
  text-transform: capitalize;
  font-size: 16px;
  margin: 0;
  padding: 0;
  outline: none;
  box-sizing: border-box;
  color: var(--white);
  margin-left: 10px;
}

.video_icon {
  width: 23px;
  height: 21px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url("data:image/svg+xml,%3Csvg width='23' height='21' viewBox='0 0 23 21' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.94745 5.1875H13.4738C14.1717 5.1875 14.8411 5.45089 15.3346 5.91973C15.8281 6.38857 16.1053 7.02446 16.1053 7.6875V15.1875C16.1053 15.3533 16.036 15.5122 15.9126 15.6294C15.7893 15.7467 15.6219 15.8125 15.4474 15.8125H4.92113C4.22319 15.8125 3.55384 15.5491 3.06032 15.0803C2.56681 14.6114 2.28955 13.9755 2.28955 13.3125V5.8125C2.28955 5.64674 2.35886 5.48777 2.48224 5.37056C2.60562 5.25335 2.77296 5.1875 2.94745 5.1875V5.1875Z' stroke='white' stroke-width='1.6875' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M16.105 9.25L20.7102 6.75V14.25L16.105 11.75' stroke='white' stroke-width='1.6875' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
}

.select_dropdown {
  right: 0;
  top: 34px;
  margin: 10px;
  z-index: 101;
  position: absolute;
  width: 190px;
  padding: 1rem;
  background-color: white;
  border: 1px solid #C7C4C1;
  border-radius: .75rem;
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
  list-style: none;
}

.select_dropdown li {
  padding: .25rem 0;
}

.select_dropdown li a {
  display: block;
  padding: 0.25rem 0.5rem;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: -0.1px;
  border-radius: 0.5rem;
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  color: var(--primary-font-color);
}

.select_dropdown li a:hover {
  background-color: #FFEEEA;
}

.select_dropdown li hr {
  margin: 0.5rem 0;
}

.select_dropdown li a i,
.select_dropdown li a svg {
  height: 1rem;
  width: 1rem;
  margin-right: 0.5rem;
}

.select_dropdown li a span i {
  margin-right: 0;
}

.select_dropdown li a span {
  color: var(--primary-font-color);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.table_wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: 80%;
}

.keywords_table {
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
  table-layout: fixed;
}

.keywords_table.head {
  width: 100%;
  height: 15%;
}

.keywords_table thead td {
  padding: 0rem .25rem 1rem;
  vertical-align: bottom;
}

.keywords_table tbody tr td {
  padding: 1rem .25rem;
}

.keywords_table tbody tr td:last-of-type {
  padding: 1rem .25rem;
}

.keywords_table tr td:nth-of-type(2) {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow-x: hidden;
}

.keywords_table tr td:not(:nth-of-type(1)):not(:nth-of-type(2)) {
  text-align: center;
}

.keywords_table thead td span {
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  letter-spacing: -0.25px;
  margin-right: 0.25rem;
}

.keywords_table tbody tr:nth-of-type(2n+1) {
  background: var(--table-secondary-color);
}

.sort_title {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.sort_title.center {
  justify-content: center;
}

.sort_title.desc i {
  transform: rotate(180deg);
}

.table_body_wrap {
  max-height: 85%;
  overflow-y: scroll;
}

.table_link {
  color: var(--primary-color);
}

.position_change {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
}

.position_change span {
  margin: 0 5px;
}

.position_change.increase {
  color: #37B2A8;
}

.position_change.decrease {
  color: #F34F4F;
}

.neutral {
  color: var(--secondary-font-color);
}

.arrow_down {
  margin-right: 5px;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 2.5V13.5' stroke='%23040707' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M3.5 9L8 13.5L12.5 9' stroke='%23040707' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
}

.increase_arrow {
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 12.25C9.8995 12.25 12.25 9.8995 12.25 7C12.25 4.10051 9.8995 1.75 7 1.75C4.10051 1.75 1.75 4.10051 1.75 7C1.75 9.8995 4.10051 12.25 7 12.25Z' stroke='%236ECADA' stroke-width='0.875' stroke-miterlimit='10'%3E%3C/path%3E%3Cpath d='M5.1438 6.66865L6.99995 4.8125L8.8561 6.66865' stroke='%236ECADA' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'%3E%3C/path%3E%3Cpath d='M7 9.1875V4.8125' stroke='%236ECADA' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'%3E%3C/path%3E%3C/svg%3E");
}

.decrease_arrow {
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 12.25C9.8995 12.25 12.25 9.8995 12.25 7C12.25 4.10051 9.8995 1.75 7 1.75C4.10051 1.75 1.75 4.10051 1.75 7C1.75 9.8995 4.10051 12.25 7 12.25Z' stroke='%23F34F4F' stroke-width='0.875' stroke-miterlimit='10'%3E%3C/path%3E%3Cpath d='M5.1438 7.33136L6.99995 9.18751L8.8561 7.33136' stroke='%23F34F4F' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'%3E%3C/path%3E%3Cpath d='M7 4.8125V9.1875' stroke='%23F34F4F' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'%3E%3C/path%3E%3C/svg%3E");
}

.view_analysis, .refresh {
  margin: 0 auto;
  width: 40px;
  display: flex;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 12px;
}
</style>
